import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AlertOptions } from '@ionic/core';
import structuredClone from '@ungap/structured-clone';
import { from, throwError } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { TaskForProcessingResponse } from 'src/app/task-process/shared/model';
import { TagsContext } from 'src/app/task-process/shared/tags-context.class';
import { Task } from 'src/app/tasks/shared/models';

import { TaskActionsResponse } from '../../task-process/public-context-and-task-actions/task-actions-log/model';
import { TagCompany, TagGroupCompany } from '../models/company';
import { Tag, TagGroup, TaskState } from '../models/misc.model';
import { API_URL_PREFIX, BasicService } from './basic.service';
import { ErrorHandlingService } from './error-handling.service';

@Injectable({
  providedIn: 'root',
})
export class TagsService {
  constructor(
    private http: HttpClient,
    private basicService: BasicService,
    private errorHandlingService: ErrorHandlingService,
    private alcmeonContextService: AlcmeonContextService
  ) {}

  /**
   * Updates one or several tags.
   *
   * This call fails with a warning from the backend if:
   * - the message is assigned to the logged user and we are trying to route to a team they don't belong to,
   * - or the message is assigned to another user which doesn't belong to the team we are trying to route to.
   * In both cases, we display a confirmation request, and if confirmed, we force the tag update.
   */
  updateTagValue(taskId: number, tagIds: number[], force) {
    const data = {
      tags_ids: tagIds,
      force,
      should_show_warning_dialog: true,
    };
    const taskTagURL = `${API_URL_PREFIX}/${this.alcmeonContextService.companyId}/task/${taskId}/tags`;
    return this.http.post<any>(taskTagURL, data).pipe(this.handleTagUpdateError(taskId, tagIds));
  }

  /**
   * Updates the tagGroups and the routingTagGroup on the response with the corresponding selectedTagId of the task
   */
  updateTagGroups(response: TaskForProcessingResponse | TaskActionsResponse, taskProp: string): void {
    response.tagGroups.forEach(tagGroup => {
      tagGroup.tags.forEach(tag => {
        if (response[taskProp].tagIds.includes(tag.id) && !tag.isCategory) {
          tagGroup.selectedTagId = tag.id;
        }
      });
    });
    if (response[taskProp].routingTagId) {
      response.routingTagGroup.selectedTagId = response[taskProp].routingTagId;
    }
  }

  private handleTagUpdateError(taskId: number, tagIds: number[]) {
    return catchError(error => {
      let catchResult;
      if (this.errorRequiresConfirmation(error)) {
        // get user confirmation as a Promise, convert that Promise to an Observable,
        // and mergMap that Observable with a new call to updateTagValue()
        catchResult = from(this.confirmRouting(error)).pipe(mergeMap(() => this.updateTagValue(taskId, tagIds, true)));
      } else {
        const knownErrorReasons = {
          'invalid-tag-id-not-active': 'm/task-process/error/tag-deleted',
        };
        this.errorHandlingService.alertHttpError(error, knownErrorReasons);
        catchResult = throwError(error);
      }
      return catchResult;
    });
  }

  private errorRequiresConfirmation(response: HttpErrorResponse) {
    const reasons = ['routing-tag-not-allowed-for-self', 'routing-tag-not-allowed-for-other'];
    return response.error.error && reasons.includes(response.error.error.reason);
  }

  private warningMessageKey(transferToAnotherTeam: boolean) {
    return transferToAnotherTeam
      ? 'm/task-process/routing-tag/confirm-self/text'
      : 'm/task-process/routing-tag/confirm-other/text';
  }

  /**
   * Asks confirmation for tag update, with a warning message corresponding to the given error response.
   */
  private confirmRouting(errorResponse): Promise<any> {
    const { data, reason } = errorResponse.error.error;
    const { email, previous_state } = data;
    const inbox = this.basicService.translate(`m/task-process/previous_state/${previous_state}`);
    const transferToAnotherTeam = reason === 'routing-tag-not-allowed-for-self';
    const messageKey = this.warningMessageKey(transferToAnotherTeam);
    return new Promise((resolve, reject) => {
      const options = this.confirmationAlertOptions(messageKey, inbox, email, resolve, reject);
      this.basicService.presentAlert(options);
    });
  }

  private confirmationAlertOptions(messageKey, inbox, email, resolve, reject): AlertOptions {
    return {
      cssClass: 'alc-wider-ion-alert',
      message: this.basicService.translate(messageKey, { inbox, email }),
      buttons: [
        {
          text: this.basicService.translate('m/task-process/routing-tag/confirm/cancel'),
          role: 'cancel',
          handler: reject,
        },
        {
          text: this.basicService.translate('m/task-process/routing-tag/confirm/ok'),
          handler: resolve,
        },
      ],
    };
  }

  /**
   * Convert tagGroup object from company (only possibility in inbox) to PP structure.
   *
   * Tags object structure are different between company and PP.
   * As we migrate inbox, we want to use PP components, therefore have the correct structure.
   * BE should return the PP structure in inbox, be it needs some works not planned at the moment, so we have to cheat.
   *
   * 'isLanguage' property is missing in company. We could have it by comparing 'name' to translation 'tags_groups/language/name'.
   * But it's only necessary in pair with property 'languageCode', and we don't have it in company, so 'isLanguage' is not necessary.
   *
   * @param taskTags tags from task (= selected id)
   * @param tagGroups tag_groups from company
   * @returns tagGroups with PP structure
   */
  private convertCompanyTagsIntoTaskProcessFormat(taskTags: TagCompany[], tagGroups: TagGroupCompany[]): TagGroup[] {
    // build map of selected tags, to avoid a find() in each loop
    const selectedTagsMap: Map<number, number> = new Map();
    taskTags.forEach(tag => {
      if (tag?.id && tag.group?.id) {
        selectedTagsMap.set(tag.group.id, tag.id);
      }
    });

    const convertedTagGroups: TagGroup[] = tagGroups.map(tagGroup => {
      const { depends_upon, display_order, id, is_mandatory, is_routing, name, none_tag, tags_and_categories } =
        tagGroup;

      const convertedTagGroup: TagGroup = {
        dependsUpon: depends_upon !== undefined ? depends_upon : null,
        displayOrder: display_order,
        id,
        isMandatory: is_mandatory,
        isRouting: !!is_routing,
        name,
        noneTagId: none_tag.id,
        selectedTagId: null,
        tags: [],
      };

      convertedTagGroup.tags = tags_and_categories.map(tagAndCat => {
        const { depends_upon, id, is_category, name } = tagAndCat;
        return {
          dependsUpon: depends_upon !== undefined ? depends_upon : null,
          id,
          isCategory: is_category,
          name,
        } as Tag;
      });

      convertedTagGroup.selectedTagId = selectedTagsMap.get(convertedTagGroup.id);

      return convertedTagGroup;
    });

    return convertedTagGroups.sort((a, b) => a.displayOrder - b.displayOrder);
  }

  /**
   * Build tags to be abble to implement TagsContext in inbox.
   * See convertCompanyTagsIntoTaskProcessFormat() comments for more details.
   */
  getTagContextForInbox(task: Task): TagsContext {
    const company = this.alcmeonContextService.getCompany();
    const tagsGroups = structuredClone(company.tags_groups);

    const allTagGroup = this.convertCompanyTagsIntoTaskProcessFormat(task.tags, tagsGroups);
    const routingTagIndex = allTagGroup.findIndex(convertedTag => convertedTag.isRouting);
    const routingTagGroup = allTagGroup[routingTagIndex];
    allTagGroup.splice(routingTagIndex, 1);

    return new TagsContext(
      allTagGroup,
      routingTagGroup,
      task.id,
      task.state.replace(/_/g, '-') as TaskState, // diff between inbox (_) an PP (-) ?!! :(
      null
    );
  }
}
